import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { CIPXQuoteRequestIntakeFormComponent } from '@sbd-doug-frontend/shared/lib/quote/requests/intake/form/intake-form.component';
import { IntakeFormStep } from '@sbd-doug-frontend/shared/lib/quote/requests/intake/form/intake-form.types';
import { MatButtonModule } from '@angular/material/button';
import { DRAWER_DATA, DRAWER_REF, DrawerRef } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { CIPXDrawerContentInterface } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.interface';
import { CIPXQuoteRequestIntakeFormService } from '@sbd-doug-frontend/shared/lib/quote/requests/intake/form/intake-form.service';
import { OpenQuoteRequestsWidgetService } from '../../dashboard/open-quote-requests-widget/open-quote-requests-widget.service';
import { LoggingService } from '@sbd-doug-frontend/shared/lib/shared-services/logging.service';
import { stages, updateStageConfig } from '@sbd-doug-frontend/shared/lib/quote/requests/intake/form/steps/intake-form-bond-lookup/stage-config';

// Define the data shape this component expects
export interface QuoteRequestDrawerData {
 // Example - allow injecting a prefilled state selection.
}

@Component({
  selector: 'sbd-account-portal-frontend-quote-request-intake-form-drawer-wrapper',
  templateUrl: './quote-request-intake-form-drawer-wrapper.component.html',
  styleUrl: './quote-request-intake-form-drawer-wrapper.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    CIPXQuoteRequestIntakeFormComponent,
    MatButtonModule
  ]
})
export class QuoteRequestIntakeFormDrawerWrapperComponent implements CIPXDrawerContentInterface<QuoteRequestDrawerData>, OnInit {
  private _intakeFormService = inject(CIPXQuoteRequestIntakeFormService);
  private _openQuoteRequestWidgetService = inject(OpenQuoteRequestsWidgetService);
  @Output() closeRequested = new EventEmitter<void>();
  private _loggingService = inject(LoggingService);

  currentStep?: IntakeFormStep;

  // Inject typed data - now properly typed!
  data = inject(DRAWER_DATA, { optional: true }) as QuoteRequestDrawerData | null;

  // Inject drawer ref to control the drawer - now properly typed!
  drawerRef = inject(DRAWER_REF, { optional: true }) as DrawerRef<QuoteRequestIntakeFormDrawerWrapperComponent> | null;

  ngOnInit(): void {
    this.drawerRef?.setShowBackButton(false);
    this.drawerRef?.setTitle('Who\'s this bond for?');
    this._intakeFormService.goToStep(IntakeFormStep.PrincipalSelection);
    this._intakeFormService.resetData();
  }

  onIntakeFormStepChanged(step: IntakeFormStep) {
    this.currentStep = step;

    // Update drawer title based on step
    if (this.drawerRef) {
      switch (step) {
        case IntakeFormStep.PrincipalSelection:
          this.drawerRef.setTitle('Who\'s this bond for?');
          this.drawerRef.setShowBackButton(false);
          break;
        case IntakeFormStep.BondLookup:
          this.drawerRef.setTitle('Find Your Bond');
          this.drawerRef.setShowBackButton(true);
          break;
        case IntakeFormStep.RequestDetails:
          this.drawerRef.setTitle('Bond Information');
          this.drawerRef.setShowBackButton(true);
          break;
        case IntakeFormStep.AdditionalQuestions:
          this.drawerRef.setTitle('Additional Details');
          this.drawerRef.setShowBackButton(true);
          break;
        case IntakeFormStep.Notifications:
          this.drawerRef.setTitle('Notifications');
          this.drawerRef.setShowBackButton(true);
          break;
        case IntakeFormStep.Confirmation:
          this.drawerRef.setTitle('Thank You');
          this.drawerRef.setShowBackButton(false);
          break;
      }
    }
  }

  onIntakeFormComplete() {
    this._openQuoteRequestWidgetService.getOpenQuoteRequests().subscribe();
  }

  onCancelRequestQuote() {
    this.resetFormAndStages();
    this.closeDrawer();
  }

  onConfirmationCloseRequested() {
    // When confirmation step requests close, reset and close the drawer
    this.resetFormAndStages();
    this.closeDrawer();
  }

  closeDrawer() {
    this.closeRequested.emit();
}

  /**
   * Resets the form data and bond lookup stages to their default state
   */
  private resetFormAndStages(): void {
    // Reset the service data and emit reset form event
    this._intakeFormService.resetData();
    this._intakeFormService.resetForm.emit();

    // Reset all bond lookup stages to their default state
    stages.forEach(stageConfig => {
      updateStageConfig(stageConfig, stageConfig.defaultActive, true);
    });

    // Go back to the first step
    this._intakeFormService.goToStep(IntakeFormStep.PrincipalSelection);
  }

  // Implement CIPXDrawerContentInterface methods
  onBackClick() {
    // Handle back navigation based on current step
    switch (this.currentStep) {
      case IntakeFormStep.BondLookup:
        this._intakeFormService.goToStep(IntakeFormStep.PrincipalSelection);
        break;
      case IntakeFormStep.RequestDetails:
        this._intakeFormService.goToStep(IntakeFormStep.BondLookup);
        break;
      case IntakeFormStep.AdditionalQuestions:
        this._intakeFormService.goToStep(IntakeFormStep.RequestDetails);
        break;
      case IntakeFormStep.Notifications:
        this._intakeFormService.goToStep(IntakeFormStep.AdditionalQuestions);
        break;
      case IntakeFormStep.Confirmation:
        this._intakeFormService.resetData();
        this._intakeFormService.goToStep(IntakeFormStep.PrincipalSelection);
        break;
    }
  }

  canClose(): boolean {
    // Check if there are unsaved changes
    // For now, always allow closing
    return true;
  }

  afterDrawerOpened(): void {
    // Initialize any data or focus elements if needed
  }

  beforeDrawerClosed(): void {
    // Reset form and stages when drawer is closed via X button
    this.resetFormAndStages();
  }
}
